import { Dispatch, MutableRefObject, SetStateAction } from "react";

import { TreeViewPublicAPI, UseTreeViewExpansionSignature, UseTreeViewFocusSignature, UseTreeViewIconsSignature, UseTreeViewKeyboardNavigationSignature, UseTreeViewLabelSignature, UseTreeViewSelectionSignature } from "@mui/x-tree-view/internals";
import { UseTreeViewItemsPublicAPI, UseTreeViewItemsSignature } from "@mui/x-tree-view/internals/plugins/useTreeViewItems/useTreeViewItems.types";

import { CustomTreeViewBaseItem, FileState } from "../../utils/models/common-models";
import { FileType } from "./FileExplorer";


export const addItemToFileExplorerTree = (
    tree: CustomTreeViewBaseItem[],
    parentId: string | null | undefined,
    newItem: CustomTreeViewBaseItem
): CustomTreeViewBaseItem[] => {

    if (!parentId) {
        return [...tree, { ...newItem, parentId: null }]
    }

    return tree.map((item) => {
        if (item.id === parentId && item.type === 'folder') {
            const newChild = { ...newItem, parentId };
            return {
                ...item,
                children: item.children ? [...item.children, newChild] : [newChild]
            };
        } else if (item.children) {
            const updatedChildren = addItemToFileExplorerTree(item.children, parentId, newItem);
            return {
                ...item,
                children: updatedChildren
            };
        }
        return item;
    });
}


export const handleCreateNewFolder = (
    selectedFile: string,
    newFolderName: string,
    apiTreeRef: MutableRefObject<TreeViewPublicAPI<readonly [UseTreeViewItemsSignature, UseTreeViewExpansionSignature, UseTreeViewSelectionSignature, UseTreeViewFocusSignature, UseTreeViewKeyboardNavigationSignature, UseTreeViewIconsSignature, UseTreeViewLabelSignature]> | undefined>,
    setFileStructure: Dispatch<SetStateAction<CustomTreeViewBaseItem[]>>,
    setIsDialogOpen: Dispatch<SetStateAction<boolean>>,
    setNewFolderName: Dispatch<SetStateAction<string>>
) => {
    if (!selectedFile) return alert("Please select an item first.");

    if (newFolderName.trim()) {
        console.log("creating new folder: ", newFolderName);
        const selectedTreeItem: CustomTreeViewBaseItem = (apiTreeRef.current! as UseTreeViewItemsPublicAPI<any>)?.getItem(selectedFile);
        const newItemId = (selectedTreeItem?.type === 'file' && !selectedTreeItem.parentId) ?
            newFolderName :
            `${selectedTreeItem?.type === 'folder' ? selectedTreeItem.id : selectedTreeItem.parentId}/${newFolderName}`;
        const isItemPresent: CustomTreeViewBaseItem = (apiTreeRef.current! as UseTreeViewItemsPublicAPI<any>)?.getItem(newItemId);

        if (isItemPresent && isItemPresent.id) {
            return alert('Name already used, please use some other name.')
        }

        // Create a new folder object
        const newFolder: CustomTreeViewBaseItem = {
            id: newItemId,
            label: newFolderName,
            type: "folder",
            fileType: 'pinned',
            parentId: "",
            children: [],
        };

        // Update the file structure
        setFileStructure((prevStructure) => addItemToFileExplorerTree(
            prevStructure,
            selectedTreeItem?.type === 'folder' ? selectedTreeItem.id : selectedTreeItem?.parentId,
            newFolder
        ));

        // Close the dialog and reset the input
        setIsDialogOpen(false);
        setNewFolderName('');
    }
};


export const handleAddNewFile = (
    selectedFile: string,
    newFileName: string,
    apiTreeRef: MutableRefObject<TreeViewPublicAPI<readonly [UseTreeViewItemsSignature, UseTreeViewExpansionSignature, UseTreeViewSelectionSignature, UseTreeViewFocusSignature, UseTreeViewKeyboardNavigationSignature, UseTreeViewIconsSignature, UseTreeViewLabelSignature]> | undefined>,
    supportedFileType: { [key: string]: FileType },
    setFileStructure: Dispatch<SetStateAction<CustomTreeViewBaseItem[]>>,
    setIsDialogOpen: Dispatch<SetStateAction<boolean>>,
    setNewFileName: Dispatch<SetStateAction<string>>,
    file_name?: string,
) => {
    if (!selectedFile) return alert("Please select an item first.");

    const updatedFileName = newFileName || file_name;

    if (updatedFileName && updatedFileName.trim()) {
        console.log("creating new file: ", updatedFileName)
        const selectedTreeItem: CustomTreeViewBaseItem = (apiTreeRef.current! as UseTreeViewItemsPublicAPI<any>)?.getItem(selectedFile);
        const newItemId = (selectedTreeItem?.type === 'file' && !selectedTreeItem.parentId) ?
            updatedFileName :
            `${selectedTreeItem?.type === 'folder' ? selectedTreeItem.id : selectedTreeItem?.parentId}/${updatedFileName}`;
        const isItemPresent: CustomTreeViewBaseItem = (apiTreeRef.current! as UseTreeViewItemsPublicAPI<any>)?.getItem(newItemId);

        if (isItemPresent && isItemPresent.id) {
            return alert('Name already used, please use some other name.')
        }

        // Create a new file object
        const newFile: CustomTreeViewBaseItem = {
            id: newItemId,
            label: updatedFileName,
            type: "file",
            fileType: supportedFileType[updatedFileName.split('.')[1]] || 'file',
            parentId: "",
        };

        // Update the file structure
        setFileStructure((prevStructure) => addItemToFileExplorerTree(
            prevStructure,
            selectedTreeItem?.type === 'folder' ? selectedTreeItem.id : selectedTreeItem?.parentId,
            newFile
        ));

        // Close the dialog and reset the input
        setIsDialogOpen(false);
        setNewFileName('');
    }
};


const _handleUpdateTreeItemName = (
    tree: CustomTreeViewBaseItem[],
    itemId: string,
    newName: string,
    setItemsToDeletePath: Dispatch<SetStateAction<string[]>>,
): CustomTreeViewBaseItem[] => {
    const updateItemPath = (item: CustomTreeViewBaseItem, parentPath: string | null): CustomTreeViewBaseItem => {
        const isTarget = item.id === itemId;
        const newLabel = isTarget ? newName : item.label;
        const newId = parentPath ? `${parentPath}/${newLabel}` : newLabel;

        if (item.type === 'file') setItemsToDeletePath((prevPaths) => [...prevPaths, item.id]);

        const updatedItem: CustomTreeViewBaseItem = {
            ...item,
            id: newId,
            ...(isTarget && { label: newName }),
            path: newId,
            ...(parentPath && { parentId: parentPath }),
        };

        if (item.children) {
            updatedItem.children = item.children.map((child: CustomTreeViewBaseItem) =>
                updateItemPath(child, newId)
            );
        }

        return updatedItem;
    };

    return tree.map(item => {
        if (item.id === itemId) {
            const parentPath = item.parentId || null;
            return updateItemPath(item, parentPath);
        }

        if (item.children) {
            return { ...item, children: _handleUpdateTreeItemName(item.children, itemId, newName, setItemsToDeletePath) };
        }

        return item;
    });
};


export const handleEditTreeItemCallBack = (
    selectedFile: string,
    apiTreeRef: MutableRefObject<TreeViewPublicAPI<readonly [UseTreeViewItemsSignature, UseTreeViewExpansionSignature, UseTreeViewSelectionSignature, UseTreeViewFocusSignature, UseTreeViewKeyboardNavigationSignature, UseTreeViewIconsSignature, UseTreeViewLabelSignature]> | undefined>,
    setFileStructure: Dispatch<SetStateAction<CustomTreeViewBaseItem[]>>,
    isAddingFolder: boolean,
    newFolderName: string,
    newFileName: string,
    setFileStates: Dispatch<SetStateAction<FileState>>,
    setItemsToDeletePath: Dispatch<SetStateAction<string[]>>,
) => {
    const oldName = selectedFile;
    const selectedTreeItem: CustomTreeViewBaseItem = (apiTreeRef.current! as UseTreeViewItemsPublicAPI<any>)?.getItem(selectedFile);

    const newName = `${selectedTreeItem.parentId ? selectedTreeItem.parentId + '/' : ''}${isAddingFolder ? newFolderName : newFileName}`;
    const isItemPresent: CustomTreeViewBaseItem = (apiTreeRef.current! as UseTreeViewItemsPublicAPI<any>)?.getItem(newName);

    if (isItemPresent && isItemPresent.id) {
        return alert('Name already used, please use some other name.')
    }

    setFileStructure((prevStructure) => _handleUpdateTreeItemName(
        prevStructure,
        selectedFile,
        isAddingFolder ? newFolderName : newFileName,
        setItemsToDeletePath
    ));

    _updateFileStatesKeys(oldName, newName, setFileStates);
    return newName;
}


const _updateFileStatesKeys = (oldKey: string, newKey: string, setFileStates: Dispatch<SetStateAction<FileState>>) => {
    setFileStates((prevState) => {
        const updatedFileStates: FileState = {};

        for (const key in prevState) {
            if (key.startsWith(oldKey)) {
                const updatedKey = key.replace(oldKey, newKey);
                updatedFileStates[updatedKey] = prevState[key];
            } else {
                updatedFileStates[key] = prevState[key];
            }
        }

        return updatedFileStates;
    })
}


export const deleteFileStatesKeys = (keyToDelete: string, setFileStates: Dispatch<SetStateAction<FileState>>,) => {
    setFileStates((prevState) => {
        const updatedFileStates: FileState = {};

        for (const key in prevState) {
            if (!key.startsWith(keyToDelete)) {
                updatedFileStates[key] = prevState[key];
            }
        }

        return updatedFileStates;
    })
}


export const handleDeleteTreeItem = (
    items: CustomTreeViewBaseItem[],
    idToDelete: string,
    setItemsToDeletePath: Dispatch<SetStateAction<string[]>>,
): CustomTreeViewBaseItem[] => {
    return items
        .map((item) => {
            if (item.type === "folder" && item.children) {
                return {
                    ...item,
                    children: handleDeleteTreeItem(item.children, idToDelete, setItemsToDeletePath),
                };
            }
            return item;
        })
        .filter((item) => {
            if (item.id === idToDelete) {
                (item.type === 'folder' && item.children)
                    ? item.children.forEach((child: CustomTreeViewBaseItem) => setItemsToDeletePath(prev => [...prev, child.id]))
                    : setItemsToDeletePath(prev => [...prev, item.id])
            }
            return item.id !== idToDelete;
        });
}